import { useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { useTropicalDemoKind } from 'utils/featureFlags';
import {
  JAPAN_CHEMICAL_EVENT,
  JAPAN_EARTHQUAKE_EVENT,
  JAPAN_ORG_EVENTS,
  isJapanResourceSet,
  japanFacilityIdFromResourceNotes,
  type JapanOrgEventSpec,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/japanEarthquakeNotification';
import {
  orgEventKeys,
  setRelatedOrgEvents,
  type OrgEvent,
} from 'hooks/orgEvents/orgEventHooks';
import {
  keys as resourceSetKeys,
  type ResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';

const ensureLocks = new Map<string, Promise<unknown>>();

/** Resource set id per seeded facility, once the resource hook has created them. */
type FacilityResourceIds = Map<string, string>;

function impactedIdsFor(
  spec: JapanOrgEventSpec,
  resourceIds: FacilityResourceIds
): string[] | null {
  const ids = spec.facilityIds.map((id) => resourceIds.get(id));
  if (ids.some((id) => !id)) return null;
  return ids as string[];
}

function sameIds(a: string[] | undefined, b: string[]): boolean {
  if (!a || a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

async function ensureJapanOrgEvent({
  spec,
  existing,
  groupGuid,
  userGuid,
  impactedIds,
}: {
  spec: JapanOrgEventSpec;
  existing: OrgEvent | undefined;
  groupGuid: string;
  userGuid: string;
  impactedIds: string[] | null;
}): Promise<{ event: OrgEvent; changed: boolean }> {
  if (!existing) {
    const created = await axios.post<OrgEvent>(config.apiGateway.org_events, {
      group_guid: groupGuid,
      name: spec.name,
      type: spec.type,
      status: 'Active',
      severity: 'High',
      region: spec.region,
      business_unit: 'US Forces Japan',
      summary: spec.summary,
      resources_committed: spec.resourcesCommitted,
      lon: spec.lon,
      lat: spec.lat,
      started_at: spec.startedAt,
      creation_kind: 'threshold',
      created_by_user_guid: userGuid,
      threshold_description: spec.thresholdDescription,
      demo_key: spec.demoKey,
      ...(impactedIds ? { impacted_resource_ids: impactedIds } : {}),
    });
    return { event: created.data, changed: true };
  }

  const impactedStale =
    !!impactedIds && !sameIds(existing.impacted_resource_ids, impactedIds);
  const needsPatch =
    impactedStale ||
    existing.name !== spec.name ||
    existing.summary !== spec.summary ||
    existing.resources_committed !== spec.resourcesCommitted ||
    existing.region !== spec.region ||
    existing.type !== spec.type ||
    existing.threshold_description !== spec.thresholdDescription ||
    existing.lon !== spec.lon ||
    existing.lat !== spec.lat;
  if (!needsPatch) return { event: existing, changed: false };

  const patched = await axios.patch<OrgEvent>(
    `${config.apiGateway.org_events}/${existing.id}`,
    {
      name: spec.name,
      summary: spec.summary,
      resources_committed: spec.resourcesCommitted,
      region: spec.region,
      type: spec.type,
      threshold_description: spec.thresholdDescription,
      lon: spec.lon,
      lat: spec.lat,
      ...(impactedStale ? { impacted_resource_ids: impactedIds } : {}),
    }
  );
  return { event: patched.data, changed: true };
}

async function ensureJapanOrgEventsForGroup({
  groupGuid,
  userGuid,
  resourceIds,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  resourceIds: FacilityResourceIds;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<boolean> {
  const resp = await axios.get<OrgEvent[]>(config.apiGateway.org_events, {
    params: { groupGuid },
  });
  const existingEvents = resp.data ?? [];

  let changed = false;
  const byKey = new Map<string, OrgEvent>();
  let allImpactedSet = true;
  for (const spec of JAPAN_ORG_EVENTS) {
    const impactedIds = impactedIdsFor(spec, resourceIds);
    if (!impactedIds) allImpactedSet = false;
    const result = await ensureJapanOrgEvent({
      spec,
      existing: existingEvents.find((event) => event.demo_key === spec.demoKey),
      groupGuid,
      userGuid,
      impactedIds,
    });
    byKey.set(spec.demoKey, result.event);
    if (result.changed) changed = true;
  }

  const quake = byKey.get(JAPAN_EARTHQUAKE_EVENT.demoKey);
  const chem = byKey.get(JAPAN_CHEMICAL_EVENT.demoKey);
  if (quake && chem && !quake.related_event_ids?.includes(chem.id)) {
    await setRelatedOrgEvents(quake.id, [
      ...(quake.related_event_ids ?? []),
      chem.id,
    ]);
    changed = true;
  }

  if (changed) {
    await queryClient.invalidateQueries({
      queryKey: orgEventKeys.list(groupGuid),
    });
  }
  return allImpactedSet;
}

/**
 * Seeds the Tokyo Bay earthquake and the Keihin chemical release as two org
 * events tagged as related. Each event's impacted assets are filled in once the
 * resource hook has seeded the facilities, so this re-runs when they appear.
 */
export function useEnsureJapanOrgEvent() {
  const japanEnabled = useTropicalDemoKind() === 'japan';
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);
  const { data: resourceSets } = useQuery({
    queryKey: resourceSetKeys.list(groupGuid, userGuid),
    queryFn: async () => {
      const resp = await axios.get<ResourceSet[]>(
        `${config.apiGateway.ics_resource_sets}/${groupGuid}`
      );
      return resp.data;
    },
    enabled: japanEnabled && !!groupGuid,
  });

  const resourceIds = useMemo(() => {
    const ids: FacilityResourceIds = new Map();
    for (const resource of resourceSets ?? []) {
      if (!isJapanResourceSet(resource)) continue;
      const facilityId = japanFacilityIdFromResourceNotes(resource.notes);
      if (facilityId) ids.set(facilityId, resource.resource_set_id);
    }
    return ids;
  }, [resourceSets]);

  useEffect(() => {
    if (!japanEnabled || !groupGuid || !userGuid) {
      return;
    }
    if (completedRef.current === groupGuid) {
      return;
    }

    const lockKey = `japan-org-events:${groupGuid}`;
    let cancelled = false;

    const run = async (): Promise<void> => {
      const existing = ensureLocks.get(lockKey);
      if (existing) {
        // The in-flight run may predate the facility ids this effect saw.
        await existing.catch(() => undefined);
        if (!cancelled) return run();
        return;
      }

      const promise = ensureJapanOrgEventsForGroup({
        groupGuid,
        userGuid,
        resourceIds,
        queryClient,
      }).finally(() => {
        ensureLocks.delete(lockKey);
      });
      ensureLocks.set(lockKey, promise);

      try {
        const complete = await promise;
        if (!cancelled && complete) completedRef.current = groupGuid;
      } catch (error) {
        console.error('Failed to ensure Japan Tokyo Bay org events', error);
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [japanEnabled, groupGuid, userGuid, resourceIds, queryClient]);
}
